import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { getUserGameDataSummary, getUserGameData } from '../api/userGameData';
import { spinEnergyAPI } from '../api/spinEnergy';

const GameDataContext = createContext();

export const useGameDataContext = () => {
    const context = useContext(GameDataContext);
    if (!context) {
        throw new Error('useGameDataContext must be used within a GameDataProvider');
    }
    return context;
};

// События, которые должны инициировать обновление игровых данных
const GAME_DATA_UPDATE_EVENTS = {
    // Энергия
    ENERGY_SPENT: 'energy_spent',
    ENERGY_ADDED: 'energy_added',
    ENERGY_SPIN: 'energy_spin',
    
    // Опыт и уровень
    EXPERIENCE_ADDED: 'experience_added',
    LEVEL_UP: 'level_up',
    
    // Задания
    QUEST_COMPLETED: 'quest_completed',
    QUEST_PROGRESS: 'quest_progress',
    
    // Достижения
    ACHIEVEMENT_UNLOCKED: 'achievement_unlocked',
    
    // Уведомления
    NOTIFICATION_READ: 'notification_read',
    
    // Производство
    PRODUCTION_COMPLETED: 'production_completed',
    PRODUCTION_STARTED: 'production_started',
    
    // Ручное обновление
    MANUAL_REFRESH: 'manual_refresh'
};

export const GameDataProvider = ({ children }) => {
    const queryClient = useQueryClient();
    const [lastUpdateTime, setLastUpdateTime] = useState(Date.now());
    const [isUpdating, setIsUpdating] = useState(false);

    // Основные игровые данные (summary)
    const { 
        data: gameDataSummary, 
        isLoading: summaryLoading, 
        error: summaryError,
        refetch: refetchSummary
    } = useQuery('gameDataSummary', getUserGameDataSummary, {
        staleTime: 5 * 60 * 1000, // 5 минут
        cacheTime: 10 * 60 * 1000, // 10 минут
        refetchOnWindowFocus: false,
        refetchOnMount: false, // Отключено - данные загружаются через Redux
        refetchOnReconnect: false,
        enabled: false // Полностью отключено - используем только Redux
    });

    // Статус энергии
    const {
        data: energyStatus,
        isLoading: energyLoading,
        error: energyError,
        refetch: refetchEnergy
    } = useQuery('energyStatus', spinEnergyAPI.getEnergyStatus, {
        staleTime: 2 * 60 * 1000, // 2 минуты (энергия обновляется чаще)
        cacheTime: 5 * 60 * 1000, // 5 минут
        refetchOnWindowFocus: true,
        refetchOnMount: true,
        refetchOnReconnect: true,
        enabled: true // Включено для страницы энергетического баланса
    });

    // Полные игровые данные
    const { 
        data: fullGameData, 
        isLoading: fullDataLoading, 
        error: fullDataError,
        refetch: refetchFullData
    } = useQuery('fullGameData', getUserGameData, {
        staleTime: 10 * 60 * 1000, // 10 минут
        cacheTime: 15 * 60 * 1000, // 15 минут
        refetchOnWindowFocus: false,
        refetchOnMount: false, // Не загружаем автоматически
        refetchOnReconnect: false
    });

    // Функция для принудительного обновления всех данных
    const forceRefreshAll = useCallback(async () => {
        setIsUpdating(true);
        try {
            await Promise.all([
                refetchSummary(),
                refetchEnergy(),
                refetchFullData()
            ]);
            setLastUpdateTime(Date.now());
        } catch (error) {
            console.error('Error refreshing game data:', error);
        } finally {
            setIsUpdating(false);
        }
    }, [refetchSummary, refetchEnergy, refetchFullData]);

    // Функция для обновления только энергии
    const refreshEnergyOnly = useCallback(async () => {
        try {
            await refetchEnergy();
            setLastUpdateTime(Date.now());
        } catch (error) {
            console.error('Error refreshing energy:', error);
        }
    }, [refetchEnergy]);

    // Функция для обновления только summary данных
    const refreshSummaryOnly = useCallback(async () => {
        try {
            await refetchSummary();
            setLastUpdateTime(Date.now());
        } catch (error) {
            console.error('Error refreshing summary:', error);
        }
    }, [refetchSummary]);

    // Обработчик событий обновления
    const handleGameDataUpdate = useCallback((eventType, data = {}) => {
        console.log(`🔄 Game data update event: ${eventType}`, data);
        
        switch (eventType) {
            case GAME_DATA_UPDATE_EVENTS.ENERGY_SPENT:
            case GAME_DATA_UPDATE_EVENTS.ENERGY_ADDED:
            case GAME_DATA_UPDATE_EVENTS.ENERGY_SPIN:
                // Обновляем только энергию
                refreshEnergyOnly();
                break;
                
            case GAME_DATA_UPDATE_EVENTS.EXPERIENCE_ADDED:
            case GAME_DATA_UPDATE_EVENTS.LEVEL_UP:
            case GAME_DATA_UPDATE_EVENTS.QUEST_COMPLETED:
            case GAME_DATA_UPDATE_EVENTS.ACHIEVEMENT_UNLOCKED:
                // Обновляем summary и полные данные
                refreshSummaryOnly();
                break;
                
            case GAME_DATA_UPDATE_EVENTS.MANUAL_REFRESH:
                // Принудительное обновление всех данных
                forceRefreshAll();
                break;
                
            default:
                // Для остальных событий обновляем summary
                refreshSummaryOnly();
                break;
        }
    }, [refreshEnergyOnly, refreshSummaryOnly, forceRefreshAll]);

    // Инвалидация кеша для конкретных запросов
    const invalidateCache = useCallback((queryKey) => {
        queryClient.invalidateQueries(queryKey);
    }, [queryClient]);

    // Экспортируем константы событий
    const updateEvents = GAME_DATA_UPDATE_EVENTS;

    const value = {
        // Данные
        gameDataSummary,
        energyStatus,
        fullGameData,
        
        // Состояния загрузки
        summaryLoading,
        energyLoading,
        fullDataLoading,
        
        // Ошибки
        summaryError,
        energyError,
        fullDataError,
        
        // Функции обновления
        forceRefreshAll,
        refreshEnergyOnly,
        refreshSummaryOnly,
        handleGameDataUpdate,
        invalidateCache,
        
        // Метаданные
        lastUpdateTime,
        isUpdating,
        updateEvents
    };

    return (
        <GameDataContext.Provider value={value}>
            {children}
        </GameDataContext.Provider>
    );
};
